import { endOfMonth, startOfMonth } from 'date-fns'
import { getInstrument } from 'finviz-charts/app/utils/chart'
import * as React from 'react'
import { ComposedChart } from 'recharts'

import { Quote, TIMEFRAME } from '../../../../../app/charts-lib'
import { getDate } from '../../../../../app/header/utils'
import { Bar } from '../../../../components/chart/Bar'
import { CartesianGrid } from '../../../../components/chart/CartesianGrid'
import { ChartBox } from '../../../../components/chart/ChartBox'
import { ChartWrapper } from '../../../../components/chart/ChartWrapper'
import { CursorBar } from '../../../../components/chart/Cursor'
import { Label } from '../../../../components/chart/Label'
import { Line } from '../../../../components/chart/Line'
import { Tooltip, TooltipRow } from '../../../../components/chart/Tooltip'
import { DateFormat, XAxis, dateAxisFormatter } from '../../../../components/chart/XAxis'
import { YAxis } from '../../../../components/chart/YAxis'
import { shortFormatDollar } from '../../../../util'
import { parseRouteInitData } from '../../../../util-routing'
import { QuoteContext } from '../../QuoteContext'
import { InsiderVolumeBar } from './InsiderVolumeBar'
import { BarType, CHART_HEIGHT } from './constants'
import { ChartData, TradingVolumeChartInit } from './types'
import { tooltipDataFormatter, tooltipTitleFormatter } from './utils'

export function InsiderTradingVolumeChart({ className }: { className?: string }) {
  const { ticker, index } = React.useContext(QuoteContext)
  const [chartData, setChartData] = React.useState<ChartData[]>([])

  const initData = React.useMemo(() => {
    const data = parseRouteInitData<TradingVolumeChartInit>({
      elementId: `insider-init-data-${index}`,
      removeElement: false,
    })

    return {
      data,
      domain: data?.length
        ? [
            getDate(startOfMonth(getDate(data[0].date))).getTime(),
            getDate(endOfMonth(getDate(data[data.length - 1].date))).getTime(),
          ]
        : [],
    }
  }, [index])

  const hasInsiderTradingHistory = React.useMemo(
    () => initData.data?.some((x) => x.saleTransactionCount > 0 || x.buyTransactionCount > 0),
    [initData]
  )

  React.useEffect(() => {
    if (!hasInsiderTradingHistory) return

    async function fetchQuoteData() {
      if (!initData.data?.length) return

      // We need to convert domain timestamps to seconds because quote API requires timestamps in seconds
      // and also returned data have date in seconds, but we need to convert those data to milliseconds
      // because recharts requires timestamps in milliseconds
      const quote = await Quote.get({
        ticker,
        instrument: getInstrument(ticker),
        timeframe: TIMEFRAME.d,
        shouldUseCache: false,
        // Do not reuse quote, it might not have required data, and because this is standalone part
        // we can't easily consider requirements for data in other parts of the app where quote
        // with same timeframe might be created
        options: { dateFrom: initData.domain[0] / 1000, dateTo: initData.domain[1] / 1000 },
      })

      setChartData(
        quote.close.map((close, index) => ({
          date: quote.date[index] * 1000,
          close,
          insider: initData.data?.[index],
        }))
      )
    }

    fetchQuoteData()
  }, [initData, hasInsiderTradingHistory, index, ticker])

  if (!initData.data?.length || !hasInsiderTradingHistory) return null

  return (
    <ChartBox label="Insider Trading Volume" className={className}>
      <ChartWrapper height={CHART_HEIGHT}>
        <ComposedChart data={initData.data} barGap={2} barCategoryGap={0} margin={{ left: 20, right: 20 }}>
          <CartesianGrid />
          <XAxis domain={initData.domain} xAxisId="xAxisLine" hide />
          <XAxis
            domain={initData.domain}
            tickFormatter={(value) => dateAxisFormatter({ value, outFormat: DateFormat.MonthShort, breakOnYear: true })}
          />
          <YAxis orientation="right">
            <Label dx={38}>USD Trades Volume</Label>
          </YAxis>
          <YAxis yAxisId="yAxisClose" tickFormatter={(value) => shortFormatDollar(value)} domain={['dataMin', 'auto']}>
            <Label dx={-38} color="blue">
              Close Price
            </Label>
          </YAxis>
          <Tooltip
            formatLabel={tooltipTitleFormatter}
            formatValue={tooltipDataFormatter}
            cursor={<CursorBar numberOfDatapoints={initData.data.length} />}
          >
            <TooltipRow
              label="Insider Sales Volume"
              dataKey={BarType.Sale}
              valueClass="col-start-3 col-end-5 grid grid-cols-subgrid"
            />
            <TooltipRow
              label="Insider Buy Volume"
              dataKey={BarType.Buy}
              valueClass="col-start-3 col-end-5 grid grid-cols-subgrid"
            />
          </Tooltip>

          <Line data={chartData} dataKey="close" xAxisId="xAxisLine" yAxisId="yAxisClose" />
          <Bar dataKey={BarType.Sale} color="red" shape={InsiderVolumeBar} />
          <Bar dataKey={BarType.Buy} color="green" shape={InsiderVolumeBar} />
        </ComposedChart>
      </ChartWrapper>
    </ChartBox>
  )
}
